import { Box, Card, CardActionArea, CardContent, Typography } from "@mui/material";
import { Link } from "react-router-dom";
import { Title, usePermissions } from "react-admin";
import { hasPermission } from "@/utils/permission-utils";
import {
  MADRASA_CLASSES_VIEW,
  MADRASA_FEE_SETUP_VIEW,
  MADRASA_FEES_CREATE,
  MADRASA_FEES_VIEW,
  MADRASA_ENROLLMENTS_CREATE,
  MADRASA_ENROLLMENTS_VIEW,
  MADRASA_SETTINGS_VIEW,
  MADRASA_STUDENTS_VIEW,
} from "../shared/madrasa-permissions";

const LINKS = [
  {
    to: "/madrasaStudents",
    permission: MADRASA_STUDENTS_VIEW,
    title: "Students",
    description: "Add and find students and their guardians.",
  },
  {
    to: "/madrasaEnrollments",
    permission: MADRASA_ENROLLMENTS_VIEW,
    title: "Enrollments",
    description: "See who is enrolled in which class, and enroll or withdraw students.",
  },
  {
    to: "/madrasaPromote",
    permission: MADRASA_ENROLLMENTS_CREATE,
    title: "Promote students",
    description: "Move a class into the next academic year, with a preview first.",
  },
  {
    to: "/feeCollect",
    permission: MADRASA_FEES_CREATE,
    title: "Collect fee",
    description: "Take a payment from a student and issue a receipt.",
  },
  {
    to: "/feeReceipts",
    permission: MADRASA_FEES_VIEW,
    title: "Receipts",
    description: "Find, print or void fee receipts.",
  },
  {
    to: "/feeDefaulters",
    permission: MADRASA_FEES_VIEW,
    title: "Fee defaulters",
    description: "Students with overdue fees, with guardian contact. Export to CSV.",
  },
  {
    to: "/feeCollectionSummary",
    permission: MADRASA_FEES_VIEW,
    title: "Collection summary",
    description: "Fees collected between two dates, by class and payment mode.",
  },
  {
    to: "/feeLedger",
    permission: MADRASA_FEES_VIEW,
    title: "Student ledger",
    description: "Everything one student owes and has paid.",
  },
  {
    to: "/feeHeads",
    permission: MADRASA_FEE_SETUP_VIEW,
    title: "Fee heads",
    description: "The kinds of fee you charge (tuition, exam, admission).",
  },
  {
    to: "/feeStructures",
    permission: MADRASA_FEE_SETUP_VIEW,
    title: "Fee structures",
    description: "What each class pays, and how often, for an academic year.",
  },
  {
    to: "/madrasaClasses",
    permission: MADRASA_CLASSES_VIEW,
    title: "Classes",
    description: "Set up the classes your madrasa runs.",
  },
  {
    to: "/academicYears",
    permission: MADRASA_SETTINGS_VIEW,
    title: "Academic years",
    description: "Set up the school year that enrollments and fees belong to.",
  },
  {
    to: "/madrasaSettings",
    permission: MADRASA_SETTINGS_VIEW,
    title: "Madrasa settings",
    description: "Configure fee receipt numbering.",
  },
] as const;

export default function MadrasaDashboard() {
  const { permissions } = usePermissions();
  const links = LINKS.filter((link) => hasPermission(permissions, link.permission));

  return (
    <>
      <Title title="Madrasa" />
      <Box sx={{ p: 2 }}>
        <Typography variant="h5" sx={{ mb: 0.5 }}>
          Madrasa
        </Typography>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Back-office management for the madrasa.
        </Typography>
        <Box
          sx={{
            display: "grid",
            gap: 2,
            gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))",
          }}
        >
          {links.map((link) => (
            <Card key={link.to} variant="outlined">
              <CardActionArea component={Link} to={link.to}>
                <CardContent>
                  <Typography variant="subtitle1">{link.title}</Typography>
                  <Typography variant="body2" color="text.secondary">
                    {link.description}
                  </Typography>
                </CardContent>
              </CardActionArea>
            </Card>
          ))}
        </Box>
      </Box>
    </>
  );
}
